import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { MindsetApiError, type MindsetIntegrationProvisioningOperation } from "@mindset/api-client";
import type { IntegrationProvisioningClient } from "./integrationSetupClient";

const labels: Record<MindsetIntegrationProvisioningOperation["state"], string> = {
  PENDING: "Activation queued", PROCESSING: "Preparing configuration", AWAITING_ACK: "Waiting for service acknowledgments",
  COMPLETED: "Activation request completed", FAILED: "Activation request failed", CANCELED: "Activation request canceled",
};
interface Props { companyId: string; userId: string; operationId: string; applicationId?: string; read: IntegrationProvisioningClient["getProvisioning"]; autoRefresh?: boolean; onState?: (state: MindsetIntegrationProvisioningOperation["state"] | undefined) => void }
type ReadResult = MindsetIntegrationProvisioningOperation["state"] | "RETRY" | undefined;
export function IntegrationOperationStatus(props: Props) {
  return <Status key={`${props.companyId}:${props.userId}:${props.operationId}:${props.applicationId ?? ""}`} {...props} />;
}
function Status({ companyId, userId, operationId, applicationId, read, autoRefresh = false, onState }: Props) {
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const pending = useRef<Promise<ReadResult> | null>(null);
  const alive = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const check = useCallback(() => {
    if (pending.current) return pending.current;
    setBusy(true); setError("");
    onState?.(undefined);
    const work = (async (): Promise<ReadResult> => {
      try {
        const operation = await Promise.resolve().then(() => read(companyId, userId, operationId));
        if (!alive.current) return;
        if (operation.id !== operationId || operation.companyId !== companyId ||
          (applicationId !== undefined && operation.applicationId !== applicationId) ||
          !Object.hasOwn(labels, operation.state)) throw new Error("Operation scope mismatch");
        setStatus(labels[operation.state]);
        onState?.(operation.state);
        return operation.state;
      } catch (failure) {
        if (alive.current) {
          setStatus("");
          setError("Activation status could not be checked.");
          if (failure instanceof MindsetApiError && failure.retryable &&
            ["http", "network", "timeout"].includes(failure.kind) &&
            (failure.status === null || failure.status === 429 || failure.status >= 500)) return "RETRY";
        }
      }
      finally { pending.current = null; if (alive.current) setBusy(false); }
    })();
    pending.current = work;
    return work;
  }, [companyId, userId, operationId, applicationId, read, onState]);
  useEffect(() => {
    if (!autoRefresh) return;
    let current = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    async function tick() {
      const state = await check();
      if (!current) return;
      if (state === "RETRY") {
        if (failures < 3) timer = setTimeout(() => void tick(), 5000 * 2 ** failures++);
      } else {
        failures = 0;
        if (state && ["PENDING", "PROCESSING", "AWAITING_ACK"].includes(state)) timer = setTimeout(() => void tick(), 5000);
      }
    }
    void tick();
    return () => { current = false; clearTimeout(timer); };
  }, [autoRefresh, check, refreshVersion]);
  return <div className="integration-operation-status">
    <button type="button" className="portal-button" disabled={busy} onClick={() => autoRefresh ? setRefreshVersion(value => value + 1) : void check()}><RefreshCw size={16} aria-hidden="true" />{busy ? "Checking activation..." : "Check activation status"}</button>
    {status ? <p role="status">{status}</p> : null}
    {error ? <p role="alert">{error}</p> : null}
  </div>;
}
